'use client';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  IConversation,
  IConversationRestrictionOptions,
  IConversationSet,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useConversationContext,
} from '@ui/components';
import { useEffect, useMemo, useState } from 'react';
import { Lightbulb, Hand, Users } from 'lucide-react';
import { UpdateConversationDetails } from './update-conversation-details';
import { ConversationPermissions } from './conversation-permission';
import { usePostHogFeatureFlag } from '@ui/hooks';
import ConversationRecipients from './converstion-recipients';
import { isCohortChatConversation } from '../../conversation-membership';
interface IProps {
  conversationSet: IConversationSet;
  restrictions_list: IConversationRestrictionOptions;
}
export const ConversationSettings = ({ conversationSet, restrictions_list }: IProps) => {
  const isReplyFeatureFlagEnabled = usePostHogFeatureFlag('2-way-messages');
  const isMessageRecipientsFlagEnabled = usePostHogFeatureFlag('message-recipient');
  const isCohortFollowFlagEnabled = usePostHogFeatureFlag('cohort-chat-membership');
  const state = useConversationContext();
  const [conversation, setConversation] = useState<IConversation>(conversationSet.conversation);
  const [restrictions, setRestrictions] =
    useState<IConversationRestrictionOptions>(restrictions_list);
  const [activeTab, setActiveTab] = useState('details');
  const isDisabled = useMemo(() => {
    return state.isUpdatingConversation;
  }, [state.isUpdatingConversation]);

  const onChangeDetails = (conversation: IConversation) => {
    setConversation((state) => ({ ...state, ...conversation }));
  };

  const onChangeRestrictions = (restrictions: IConversationRestrictionOptions) => {
    setRestrictions((state) => ({ ...state, ...restrictions }));
  };

  const handleSave = async () => {
    await state.onUpdate(conversation);
    await handleRestrictionsUpdate(conversation, restrictions);
    setConversation((state) => ({ ...state, ...conversation }));
    setRestrictions((state) => ({ ...state, ...restrictions }));
    state.setSettingsModalState(false);
  };

  const handleRestrictionsUpdate = async (
    conversation: IConversation,
    restrictions: IConversationRestrictionOptions,
  ) => await state.onUpdateConversationRestrictions(conversation, restrictions);

  const isBidirectional = useMemo(() => {
    return conversationSet?.conversation?.conversation_type === 'bidirectional';
  }, [conversationSet?.conversation?.conversation_type]);

  const conversationSelected = conversationSet?.conversation;
  const showRestrictions = Boolean(isReplyFeatureFlagEnabled && isBidirectional);
  // A cohort chat also gets this tab for its "add people who join this cohort later" switch.
  const showPermissionsTab =
    showRestrictions ||
    Boolean(isCohortFollowFlagEnabled && isCohortChatConversation(conversationSelected));

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  useEffect(() => {
    if (conversationSet?.conversation) {
      setConversation(conversationSet.conversation);
    }

    if (conversationSet?.restrictions_list) {
      setRestrictions(conversationSet.restrictions_list);
    }
  }, [conversationSet.conversation, conversationSet.restrictions_list]);

  return (
    <Dialog open={true} onOpenChange={() => state.setSettingsModalState(false)}>
      <DialogContent
        className="gap-0 bg-white p-0 sm:max-w-2xl sm:rounded"
        style={{ cursor: state.isUpdatingConversation ? 'progress' : 'default' }}>
        <DialogHeader className="border-b p-4">
          <DialogTitle className="text">Message Settings</DialogTitle>
        </DialogHeader>

        <div>
          <Tabs
            defaultValue="details"
            className="flex h-full min-h-[400px] flex-row"
            onValueChange={handleTabChange}>
            <TabsList className="bg-input flex h-full w-1/3 flex-col justify-start space-y-2 rounded-none p-2">
              <TabsTrigger
                value="details"
                className="w-full justify-start rounded-sm p-2 text-left">
                <Lightbulb className="mr-2 h-4 w-4" /> Details
              </TabsTrigger>
              {showPermissionsTab && (
                <TabsTrigger
                  value="permissions"
                  className="w-full justify-start rounded-sm p-2 text-left">
                  <Hand className="mr-2 h-4 w-4" />
                  Permissions
                </TabsTrigger>
              )}
              {conversationSelected?.id && isMessageRecipientsFlagEnabled && (
                <TabsTrigger
                  value="recipients"
                  className="w-full justify-start rounded-sm p-2 text-left">
                  <Users className="mr-2 h-4 w-4" />
                  Recipients
                </TabsTrigger>
              )}
            </TabsList>

            <div className="flex w-2/3 flex-col">
              <TabsContent value="details" className=" h-full">
                <UpdateConversationDetails
                  conversation={conversation}
                  onChangeDetails={onChangeDetails}
                />
              </TabsContent>

              {showPermissionsTab && (
                <TabsContent value="permissions" className=" h-full">
                  <ConversationPermissions
                    onChangeRestrictions={onChangeRestrictions}
                    restrictions={restrictions}
                    conversation={conversationSelected}
                    showRestrictions={showRestrictions}
                  />
                </TabsContent>
              )}

              <TabsContent value="recipients" className="h-full">
                {conversationSelected?.id && (
                  <ConversationRecipients conversation={conversationSelected} state={state} />
                )}
              </TabsContent>

              {activeTab !== 'recipients' && (
                <DialogFooter className="justify-end p-2">
                  <Button
                    type="primary"
                    size="small"
                    isLoading={state.isUpdatingConversation}
                    disabled={isDisabled}
                    onClick={handleSave}>
                    Save
                  </Button>
                </DialogFooter>
              )}
            </div>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
};
